import { Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Minus, ArrowLeft } from "lucide-react";
import Seo from "@/components/Seo";
import { absoluteUrl } from "@/lib/site-config";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { usePricing } from "@/context/PricingContext";
import { useCart } from "@/context/CartContext";

/**
 * Columnas de la comparativa, en orden.
 * `highlight` marca la columna destacada (Avanzado), igual que la card recomendada.
 */
const COLUMNS = [
  { slug: "gratis", name: "Gratis", cta: "Empezar gratis" },
  { slug: "esencial", name: "Esencial", cta: "Elegir plan" },
  { slug: "avanzado", name: "Avanzado", cta: "Elegir plan", highlight: true },
  { slug: "institucional", name: "Institucional", cta: "Contactar" },
];

/**
 * Secciones de la tabla. Cada valor de `values` es:
 *  - `true`  → incluido (✓)
 *  - `false` → no incluido (—)
 *  - string  → texto literal (p. ej. "10.000", "1 GB")
 * El orden de `values` corresponde a COLUMNS: Gratis, Esencial, Avanzado, Institucional.
 */
const SECTIONS = [
  {
    title: "Miembros y directorio",
    rows: [
      { label: "Ficha completa de cada miembro", values: [true, true, true, true] },
      { label: "Importación desde Excel", values: [true, true, true, true] },
      { label: "Filtros avanzados del directorio", values: [false, true, true, true] },
      { label: "Control de estado (activo, inactivo, visitante)", values: [false, true, true, true] },
    ],
  },
  {
    title: "Integración y crecimiento",
    rows: [
      { label: "Seguimiento de nuevos creyentes", values: [true, true, true, true] },
      { label: "Responsable asignado por cada nuevo", values: [true, true, true, true] },
    ],
  },
  {
    title: "Eventos",
    rows: [
      { label: "Calendario de eventos del equipo", values: [true, true, true, true] },
    ],
  },
  {
    title: "Ministerios y equipos",
    rows: [
      { label: "Ministerios con líderes y miembros", values: [false, true, true, true] },
      { label: "Asignación de miembros a ministerios", values: [false, true, true, true] },
    ],
  },
  {
    title: "Comunicaciones",
    rows: [
      { label: "Correos a toda la congregación", values: [false, false, true, true] },
      { label: "Correos al mes incluidos", values: [false, false, "10.000", "Ilimitados"] },
      { label: "Plantillas: bienvenidas, eventos, devocionales", values: [false, false, true, true] },
      { label: "Historial de comunicaciones", values: [false, false, true, true] },
    ],
  },
  {
    title: "Finanzas",
    rows: [
      { label: "Control de ofrendas y diezmos", values: [false, false, true, true] },
      { label: "Gastos e ingresos por área", values: [false, false, true, true] },
      { label: "Presupuestos por departamento", values: [false, false, true, true] },
    ],
  },
  {
    title: "Archivos",
    rows: [
      { label: "Almacenamiento de documentos", values: [false, false, true, true] },
      { label: "Almacenamiento incluido", values: [false, false, "1 GB", "A medida"] },
    ],
  },
  {
    title: "Reportes",
    rows: [
      { label: "Métricas de crecimiento y integración", values: [false, false, true, true] },
      { label: "Reportes para junta y liderazgo", values: [false, false, true, true] },
    ],
  },
  {
    title: "Formación",
    note: "Formaciones presenciales solo en Madrid",
    rows: [
      { label: "Formaciones incluidas", values: ["1", "3", "Personalizada", "A medida"] },
      { label: "Modalidad", values: ["Telefónica o presencial solo en Madrid", "Telefónica o presencial solo en Madrid", "Telefónica o presencial solo en Madrid", "Telefónica o presencial solo en Madrid"] },
    ],
  },
  {
    title: "Capacidad",
    rows: [
      { label: "Límite de miembros", values: ["75", "300", "1.000", "Ilimitados"] },
      { label: "Usuarios administradores", values: ["2", "10", "20", "Ilimitados"] },
    ],
  },
  {
    title: "Solo Institucional",
    rows: [
      { label: "Estructura personalizada", values: [false, false, false, true] },
      { label: "Formación inicial incluida", values: [false, false, false, true] },
      { label: "Incorporación asistida", values: [false, false, false, true] },
      { label: "Soporte prioritario garantizado", values: [false, false, false, true] },
    ],
  },
];

const fade = {
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.6 },
};

export default function ComparativaPlanesPage() {
  const navigate = useNavigate();
  const { prices } = usePricing();
  const { setPlan, setBilling } = useCart();

  // Precio de cabecera: siempre desde el backend (fuente única). Sin hardcodear.
  function headerPrice(slug) {
    if (slug === "gratis") return "Gratis";
    if (slug === "institucional") return "Personalizado";
    const p = prices && prices[slug];
    if (!p) return null;
    const val = p.founder_monthly ?? p.monthly;
    return val != null ? `Desde ${val}€/mes` : null;
  }

  // CTA por columna: mismo comportamiento que las cards de PricingSection.
  function selectPlan(slug) {
    if (slug === "gratis") {
      navigate("/#pricing");
      return;
    }
    if (slug === "institucional") {
      navigate("/contacto");
      return;
    }
    setBilling("monthly");
    setPlan(slug); // abre el CartDrawer global
  }

  function renderCell(value, highlight) {
    if (value === true) {
      return (
        <span
          className={`inline-flex h-6 w-6 items-center justify-center rounded-full ${
            highlight ? "bg-white/15 text-white" : "bg-[#EDF8F3] text-[#2D8A5E]"
          }`}
          aria-label="Incluido"
        >
          <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden />
        </span>
      );
    }
    if (value === false) {
      return (
        <span
          className={highlight ? "text-white/25" : "text-ink-4/60"}
          aria-label="No incluido"
        >
          <Minus className="h-4 w-4" aria-hidden />
        </span>
      );
    }
    return (
      <span className={`text-[14px] font-bold ${highlight ? "text-white" : "text-ink"}`}>
        {value}
      </span>
    );
  }

  return (
    <div className="w-full overflow-x-hidden bg-white text-ink">
      <Seo
        title="Comparativa de planes"
        description="Compara en detalle los planes de EkklesiaOnline: Gratis, Esencial, Avanzado e Institucional. Miembros, ministerios, comunicaciones, finanzas, reportes y más, plan por plan."
        path="/comparativa-planes"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Comparativa de planes | EkklesiaOnline",
          description:
            "Tabla comparativa completa de los planes de EkklesiaOnline por funcionalidad.",
          url: absoluteUrl("/comparativa-planes"),
        }}
      />
      <Header />

      {/* ── Header de la página ── */}
      <section className="pb-10 pt-32 md:pt-40">
        <div className="mx-auto max-w-[1760px] px-4 md:px-6 xl:px-8">
          <button
            type="button"
            onClick={() => navigate("/#pricing")}
            data-testid="comparativa-volver"
            className="inline-flex items-center gap-2 text-[15px] font-semibold text-ink-2 transition-colors duration-200 hover:text-violet-fuerte"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver a precios
          </button>
          <motion.div {...fade} className="mt-8 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-gold">
              Comparativa
            </p>
            <h1 className="font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl">
              Todos los planes, comparados en detalle
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-ink-2 md:text-xl">
              Cada funcionalidad, plan por plan. Elige el que acompaña la etapa de la
              iglesia hoy y cambia cuando lo necesites.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Tabla comparativa ── */}
      <section className="pb-24">
        <div className="mx-auto max-w-[1760px] px-4 md:px-6 xl:px-8">
          <div className="overflow-x-auto rounded-2xl border border-violet-light shadow-[0_12px_40px_-16px_rgba(74,58,138,0.16)]">
            <table
              className="w-full min-w-[760px] border-collapse text-left"
              data-testid="comparativa-tabla"
            >
              {/* Cabecera con nombre + precio + CTA por plan */}
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-white p-5 align-bottom" />
                  {COLUMNS.map((col) => {
                    const price = headerPrice(col.slug);
                    return (
                      <th
                        key={col.slug}
                        className={`p-5 text-center align-bottom ${
                          col.highlight ? "bg-ink text-white" : "bg-white"
                        }`}
                      >
                        <span
                          className={`block font-display text-lg font-bold tracking-tight ${
                            col.highlight ? "text-white" : "text-ink"
                          }`}
                        >
                          {col.name}
                        </span>
                        <span
                          className={`mt-1 block text-[13px] font-semibold ${
                            col.highlight ? "text-violet-claro" : "text-ink-3"
                          }`}
                        >
                          {price ?? " "}
                        </span>
                        <button
                          type="button"
                          onClick={() => selectPlan(col.slug)}
                          data-testid={`comparativa-cta-${col.slug}`}
                          className={`mt-4 inline-flex h-9 w-full items-center justify-center rounded-lg px-3 text-[13px] font-semibold transition-all duration-200 hover:-translate-y-0.5 ${
                            col.highlight
                              ? "bg-violet-fuerte text-white hover:bg-violet-medio"
                              : "bg-violet-fuerte text-white hover:bg-violet-oscuro"
                          }`}
                        >
                          {col.cta}
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {SECTIONS.map((section) => (
                  <Fragment key={section.title}>
                    <tr>
                      <td
                        colSpan={COLUMNS.length + 1}
                        className="border-t border-violet-light bg-violet-light/40 px-5 py-3"
                      >
                        <span className="text-[13px] font-bold uppercase tracking-[0.1em] text-violet-fuerte">
                          {section.title}
                        </span>
                        {section.note && (
                          <span className="ml-3 text-sm italic text-ink-3">
                            {section.note}
                          </span>
                        )}
                      </td>
                    </tr>
                    {section.rows.map((row) => (
                      <tr key={row.label} className="border-t border-violet-light/70">
                        <td className="sticky left-0 z-10 bg-white px-5 py-3.5 text-[14px] leading-snug text-ink-2">
                          {row.label}
                        </td>
                        {row.values.map((value, ci) => {
                          const highlight = COLUMNS[ci].highlight;
                          return (
                            <td
                              key={ci}
                              className={`px-5 py-3.5 text-center ${
                                highlight ? "bg-ink" : ""
                              }`}
                            >
                              <span className="inline-flex items-center justify-center">
                                {renderCell(value, highlight)}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-6 text-center text-[13px] text-ink-3">
            Precios con IVA incluido. Puedes cambiar de plan en cualquier momento.
          </p>

          <div className="mt-10 flex justify-center">
            <button
              type="button"
              onClick={() => navigate("/#pricing")}
              className="inline-flex items-center gap-2 text-[15px] font-semibold text-violet-fuerte transition-colors duration-200 hover:text-violet-oscuro"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver a precios
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
